import React from "react";
import DailyGoalEditIcon from "./DailyGoalEditIcon";
import dailyGoalTargetIcon from "../../../utilities/images/study_dashboard/empty_states/daily_goal_target.png";
import dailyGoalSetNowIcon from "../../../utilities/images/study_dashboard/empty_states/daily_goal_set_now.svg";
import "./DailyGoalCard.css";

const SEGMENT_COUNT = 4;

const getSegmentFillPercent = (progressPercent, segmentIndex) => {
  const segmentSize = 100 / SEGMENT_COUNT;
  const segmentStart = segmentIndex * segmentSize;
  const fill = ((progressPercent - segmentStart) / segmentSize) * 100;
  return Math.min(100, Math.max(0, fill));
};

const parseGoalCount = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return { count: "", showSolved: false, hasGoal: false };

  const match = text.match(/(\d+)\s*\/\s*(\d+)/);
  if (!match) {
    return { count: text, showSolved: false, hasGoal: true };
  }

  const target = Number(match[2]);
  return {
    count: `${match[1].padStart(2, "0")}/${match[2].padStart(2, "0")}`,
    showSolved: true,
    hasGoal: Number.isFinite(target) && target > 0,
  };
};

const DailyGoalCard = ({ goalSummary = "", progressPercent = 0, onEditGoal }) => {
  const clamped = Math.min(100, Math.max(0, Number(progressPercent) || 0));
  const { count, showSolved, hasGoal } = parseGoalCount(goalSummary);

  if (!hasGoal) {
    return (
      <article className="daily-goal-card daily-goal-card--empty">
        <p className="daily-goal-card__label">Your Daily Goal</p>
        <div className="daily-goal-card__empty-body">
          <img
            src={dailyGoalTargetIcon}
            alt=""
            className="daily-goal-card__empty-icon"
            aria-hidden="true"
          />
          <div className="daily-goal-card__empty-copy">
            <div className="daily-goal-card__empty-headline">
              <p className="daily-goal-card__empty-title">No goal set yet!</p>
              <button
                type="button"
                className="daily-goal-card__set-now"
                onClick={onEditGoal}
              >
                Set Now
                <img
                  src={dailyGoalSetNowIcon}
                  alt=""
                  className="daily-goal-card__set-now-icon"
                  aria-hidden="true"
                />
              </button>
            </div>
            <p className="daily-goal-card__empty-description">
              Set a daily goal to track your progress and stay consistent
            </p>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="daily-goal-card">
      <button
        type="button"
        className="daily-goal-card__edit"
        aria-label="Edit daily goal"
        onClick={onEditGoal}
      >
        <DailyGoalEditIcon />
      </button>

      <div className="daily-goal-card__summary">
        <p className="daily-goal-card__label">Your Daily Goal</p>
        <div className="daily-goal-card__value-row">
          <p className="daily-goal-card__count">{count || "—"}</p>
          {showSolved ? <p className="daily-goal-card__suffix">Solved</p> : null}
        </div>
      </div>

      <div className="daily-goal-card__progress">
        <div className="daily-goal-card__progress-meta">
          <span className="daily-goal-card__progress-label">Progress</span>
          <strong className="daily-goal-card__progress-value">
            {clamped ? `${clamped}%` : "—"}
          </strong>
        </div>
        <div
          className="daily-goal-card__segments"
          role="progressbar"
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Daily goal progress"
        >
          {Array.from({ length: SEGMENT_COUNT }).map((_, index) => (
            <span key={index} className="daily-goal-card__segment">
              <span
                className="daily-goal-card__segment-fill"
                style={{ width: `${getSegmentFillPercent(clamped, index)}%` }}
              />
            </span>
          ))}
        </div>
      </div>
    </article>
  );
};

export default DailyGoalCard;
